/**
 * The single typed HTTP client the companion surface talks to the edge through.
 * Constructor DI: the `fetch` implementation and `baseUrl` are injected (default
 * to the same-origin browser `fetch` behind the dev front door), so the client
 * is exercised in `bun test` against a fake `fetch` with zero network.
 *
 * Two response shapes:
 *  - JSON endpoints return `Result<T, ApiError>` (envelope-unwrapped + schema-validated).
 *  - The two SSE endpoints (`sendMessage`, `attach`) return `Result<Response, ApiError>`
 *    — the caller streams the body via `streamChatEvents`; on a pre-stream failure
 *    (over quota, not age-verified) the edge sends a JSON envelope instead, which
 *    the caller detects by content-type and unwraps to the `ApiError`.
 *
 * Every request path also RECOVERS from a `401`: the access token is short-lived,
 * so without this the whole surface dies the moment it expires. One refresh, one
 * retry, and the renewed (or lost) session goes to the injected
 * {@link SessionRecovery} sink — policy in `session-recovery.ts`, shapes here.
 *
 * Zero domain logic lives here: the client never computes quota, never decides
 * ownership — it shapes requests and validates responses. The edge is authority.
 */
import type { ApiError, Result, Tier } from '@4f/shared';
import { type ZodType, z } from 'zod';
import type {
  ApiClientOptions,
  CreateCompanionInput,
  FetchLike,
  LoginRequest,
  RegisterRequest,
  UpdateSettingsInput,
  VerifyAgeRequest,
} from './client-contract.ts';
import {
  createSessionRenewalCoordinator,
  fetchWithRecovery,
  requestJson,
  type SessionRecovery,
  type SessionRenewal,
  type SessionRenewalCoordinator,
} from './session-recovery.ts';
import {
  BillingSummaryView,
  CheckoutView,
  CompanionList,
  CompanionView,
  ConversationDetail,
  ConversationView,
  DataExportView,
  DeletionScheduleView,
  GalleryList,
  LogoutView,
  MediaJobStatusView,
  MeView,
  PrebuiltCatalogList,
  PushSubscribedView,
  ReferralSummaryView,
  RunHandle,
  SessionView,
} from './types.ts';

// The contract types stay importable from here — the module every caller already
// talks to; which file they sit in is an implementation detail.
export type * from './client-contract.ts';
export type { SessionRecovery, SessionRenewal };

const JSON_HEADERS: Readonly<Record<string, string>> = { 'content-type': 'application/json' };

export class ApiClient {
  private readonly doFetch: FetchLike;
  private readonly baseUrl: string;
  private readonly token: () => string | null;
  private readonly renewal: SessionRenewalCoordinator;
  /** The renewal each request path recovers through — `undefined` with no sink wired. */
  private readonly recover: (() => Promise<SessionRenewal>) | undefined;

  constructor(options: ApiClientOptions) {
    this.doFetch = options.fetch;
    this.baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
    this.token = options.token ?? (() => null);
    // The refresh SHAPE is the client's; the single-flight, the deadline handed
    // back in, and the "is this session dead?" policy are the recovery module's.
    this.renewal = createSessionRenewalCoordinator(
      (signal) => this.postJson('POST', '/auth/refresh', {}, signal),
      options.recovery,
    );
    this.recover = options.recovery ? () => this.renewSession() : undefined;
  }

  // --- auth (docs/idea/auth.md) ----------------------------------------------
  // Each returns a minted `SessionView`; the refresh token is set by the edge in
  // an httpOnly cookie the browser replays automatically (never touched here).
  //
  // They are also the ONLY calls that opt OUT of `401` recovery (the trailing
  // `false`): a `401` on `register`/`login` is a rejected credential, so retrying
  // would re-post the password and a failed renewal would `expire()` a session
  // alive in another tab; on `refresh`/`logout` a dead token IS the answer, so
  // recovering would recurse. `verifyAge` keeps it — that one is authenticated.

  /** POST sign-up — create the account + mint the first session. */
  async register(input: RegisterRequest): Promise<Result<SessionView, ApiError>> {
    return this.sendJson('POST', '/auth/register', input, SessionView, false);
  }

  /** POST email/password login → the access token + public user. */
  async login(input: LoginRequest): Promise<Result<SessionView, ApiError>> {
    return this.sendJson('POST', '/auth/login', input, SessionView, false);
  }

  /**
   * POST the age-verification step (the authenticated-but-ungated call that
   * clears the explicit-content gate). Returns a FRESH session whose user now
   * carries `ageOk: true`.
   */
  async verifyAge(input: VerifyAgeRequest): Promise<Result<SessionView, ApiError>> {
    return this.sendJson('POST', '/auth/verify-age', input, SessionView);
  }

  /**
   * POST refresh — exchange the httpOnly refresh cookie for a new session. The
   * boot re-hydration path: no access token needed (the cookie is authority), so
   * a reload past the short access-token lifetime still resumes the session.
   */
  async refresh(): Promise<Result<SessionView, ApiError>> {
    return this.sendJson('POST', '/auth/refresh', {}, SessionView, false);
  }

  /**
   * Renew the session behind a `401` — the SINGLE-FLIGHT primitive every request
   * path shares, and the one `realtime/ws.ts` re-dials a refused handshake
   * through. Concurrency is what it prevents: the edge treats a superseded
   * refresh token as REUSE and revokes the whole rotation family.
   */
  async renewSession(): Promise<SessionRenewal> {
    return this.renewal.renew();
  }

  /** POST logout — revoke the refresh family + clear its cookie edge-side. */
  async logout(): Promise<Result<LogoutView, ApiError>> {
    return this.sendJson('POST', '/auth/logout', {}, LogoutView, false);
  }

  // --- profile / settings ----------------------------------------------------

  /**
   * PATCH the caller's own settings (theme / locale / timezone) — persists the
   * override to the user record so it survives a reload + syncs across devices.
   * Returns the updated `MeView`; the edge is authority on the stored values.
   */
  async updateSettings(input: UpdateSettingsInput): Promise<Result<MeView, ApiError>> {
    return this.sendJson('PATCH', '/me', input, MeView);
  }

  // --- data rights (compliance.md § "Data rights") ---------------------------

  /**
   * POST the self-serve DATA EXPORT — the account's own archive (profile,
   * companions, conversations with full history, memories, media manifest,
   * ledger). Identity is the bearer, so there is nothing to pass. The edge paces
   * this per account: a `429` here means "retry shortly", not "denied".
   */
  async exportData(): Promise<Result<DataExportView, ApiError>> {
    return this.sendJson('POST', '/me/export', {}, DataExportView);
  }

  /**
   * POST the self-serve DELETION request — soft-disable now, hard purge after the
   * grace window. The explicit `confirm` flag is the edge's guard against an
   * accidental call; the CALLER is responsible for asking the user first.
   *
   * ⚠️ This response is the last one this session receives: the account is
   * disabled the instant it returns, so every subsequent request (including a
   * refresh) answers `403 account_disabled`. Drop the session on success.
   */
  async requestAccountDeletion(): Promise<Result<DeletionScheduleView, ApiError>> {
    return this.sendJson('POST', '/me/delete', { confirm: true }, DeletionScheduleView);
  }

  /** GET the GF switcher list — most-recently-active first is the edge's ordering. */
  async listCompanions(): Promise<Result<CompanionList, ApiError>> {
    return this.getJson('/companions', CompanionList);
  }

  /** GET one companion — validates it belongs to the account (the switcher's swap check). */
  async selectCompanion(girlfriendId: string): Promise<Result<CompanionView, ApiError>> {
    return this.getJson(`/companions/${encodeURIComponent(girlfriendId)}`, CompanionView);
  }

  /** POST a new conversation for a companion. */
  async createConversation(girlfriendId: string): Promise<Result<ConversationView, ApiError>> {
    return this.sendJson('POST', '/conversations', { girlfriendId }, ConversationView);
  }

  /** GET a conversation with its full history (the reopen path — replay the persisted turn). */
  async getConversation(conversationId: string): Promise<Result<ConversationDetail, ApiError>> {
    return this.getJson(`/conversations/${encodeURIComponent(conversationId)}`, ConversationDetail);
  }

  /**
   * POST a user turn: the edge gates + spawns the detached run and returns an SSE
   * VIEW of its channel. Returns the raw `Response` for the caller to stream; a
   * `402`/`403` pre-stream failure comes back JSON (caller unwraps it).
   */
  async sendMessage(
    conversationId: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<Result<Response, ApiError>> {
    return fetchWithRecovery(
      () =>
        this.doFetch(this.url(`/conversations/${encodeURIComponent(conversationId)}/messages`), {
          method: 'POST',
          headers: this.headers(JSON_HEADERS),
          body: JSON.stringify({ text }),
          signal,
        }),
      this.recover,
    );
  }

  /**
   * GET a buffered replay + live tail of a conversation's active/last run — the
   * reattach path (any device, after a tab close). `runId` targets a specific run;
   * omit it for the latest.
   */
  async attach(
    conversationId: string,
    runId?: string,
    signal?: AbortSignal,
  ): Promise<Result<Response, ApiError>> {
    const query = runId ? `?runId=${encodeURIComponent(runId)}` : '';
    return fetchWithRecovery(
      () =>
        this.doFetch(
          this.url(`/conversations/${encodeURIComponent(conversationId)}/stream${query}`),
          { method: 'GET', headers: this.headers(), signal },
        ),
      this.recover,
    );
  }

  /**
   * POST a stop for a conversation's run — `runId` targets one, omit it for the
   * latest. The run is DETACHED and server-side, so merely aborting our SSE reader
   * stops nothing: the tokens keep being generated (and paid for) and the turn is
   * billed at the end. This is the only thing that actually ends it; the outcome
   * arrives on the run's channel as `run-canceled`.
   */
  async cancelRun(conversationId: string, runId?: string): Promise<Result<RunHandle, ApiError>> {
    const query = runId ? `?runId=${encodeURIComponent(runId)}` : '';
    return this.sendJson(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/cancel${query}`,
      // The target rides the query (like `/stream`), so the body carries nothing —
      // `{}` rather than nothing at all, to keep the declared JSON content type honest.
      {},
      RunHandle,
    );
  }

  /**
   * POST an answer to a parked `waiting_input` form/choice/file directive. The
   * resumed run keeps streaming on its channel, so an open `attach` view picks it
   * up — this call is a plain ack carrying the `runId`.
   */
  async submitInput(
    conversationId: string,
    runId: string,
    value: unknown,
  ): Promise<Result<RunHandle, ApiError>> {
    return this.sendJson(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/input`,
      { runId, value },
      RunHandle,
    );
  }

  /**
   * GET a companion's Artifacts gallery — her finished assets, most-recent first,
   * each with a short-TTL presigned `url`. The lightbox opens over this full set
   * whether launched from the header button or an inline click. The edge exposes
   * this as `GET /media/gallery?girlfriendId=…` (the media surface), NOT under
   * `/companions/:id` — the companion id rides the query, matching the route in
   * `apps/api/src/routes/media.ts`.
   */
  async listGallery(girlfriendId: string): Promise<Result<GalleryList, ApiError>> {
    return this.getJson(
      `/media/gallery?girlfriendId=${encodeURIComponent(girlfriendId)}`,
      GalleryList,
    );
  }

  /**
   * POST set-as-avatar — promote one of her gallery assets to the display avatar
   * (`avatar_asset_id` only, never the identity-anchoring reference). Returns the
   * updated companion so the switcher + header re-render.
   */
  async setAvatar(girlfriendId: string, assetId: string): Promise<Result<CompanionView, ApiError>> {
    return this.sendJson(
      'POST',
      `/companions/${encodeURIComponent(girlfriendId)}/avatar`,
      { assetId },
      CompanionView,
    );
  }

  /**
   * POST a draft companion from validated builder input (the describe OR upload
   * path). Returns her `draft` view; the caller then kicks the reference build
   * ({@link buildCompanion}) or ingests a concept image ({@link uploadConcept}).
   */
  async createCompanion(input: CreateCompanionInput): Promise<Result<CompanionView, ApiError>> {
    return this.sendJson('POST', '/companions', input, CompanionView);
  }

  /** POST the describe-path build — enqueue her reference image; she goes `building`. */
  async buildCompanion(girlfriendId: string): Promise<Result<CompanionView, ApiError>> {
    return this.sendJson(
      'POST',
      `/companions/${encodeURIComponent(girlfriendId)}/build`,
      {},
      CompanionView,
    );
  }

  /**
   * POST the upload-path concept image (multipart) — the edge re-validates type +
   * size, moderates it (red line 2 — your own character concept only), then
   * attaches + builds. The browser sets the multipart `content-type` + boundary,
   * so this call sends no explicit JSON header.
   */
  async uploadConcept(girlfriendId: string, file: File): Promise<Result<CompanionView, ApiError>> {
    const form = new FormData();
    form.set('file', file);
    return this.sendForm(
      `/companions/${encodeURIComponent(girlfriendId)}/upload`,
      form,
      CompanionView,
    );
  }

  /**
   * GET the billing summary — tier, credit balance, subscription, a recent
   * statement, and the plan/pack catalog. Display-only: the billing card/modal
   * renders exactly this, it never computes a balance or tier itself.
   */
  async getBillingSummary(): Promise<Result<BillingSummaryView, ApiError>> {
    return this.getJson('/billing/summary', BillingSummaryView);
  }

  /**
   * POST a subscription checkout — the edge mints a rail-hosted (CCBill) URL for
   * the chosen paid `tier`. The account rides the bearer, never the body, and NO
   * credit is granted here: the verified webhook does that, so the caller only
   * hands the browser off to {@link CheckoutView.checkoutUrl}.
   */
  async startSubscriptionCheckout(tier: Tier): Promise<Result<CheckoutView, ApiError>> {
    return this.sendJson('POST', '/billing/checkout/subscription', { tier }, CheckoutView);
  }

  /**
   * POST a one-time top-up checkout. Only the `packId` is sent — the server
   * resolves the charged price from its own config, so a tampered client price
   * can never buy a bigger pack.
   */
  async startTopupCheckout(packId: string): Promise<Result<CheckoutView, ApiError>> {
    return this.sendJson('POST', '/billing/checkout/topup', { packId }, CheckoutView);
  }

  /**
   * POST a subscription cancellation (the consumer-protection affordance: what
   * can be bought in-app must be cancellable in-app). Validated only as "the
   * edge said ok" — the caller re-reads {@link getBillingSummary}, which is the
   * sole authority on the resulting status, so this stays correct whatever
   * acknowledgement shape the route settles on.
   */
  async cancelSubscription(): Promise<Result<unknown, ApiError>> {
    return this.sendJson('POST', '/billing/subscription/cancel', {}, z.unknown());
  }

  /**
   * GET the referral dashboard summary — your shareable code, invite/qualify
   * counts, total rev-share earned, and the histories. Display-only: the
   * referral card/modal renders exactly this, it never computes qualification
   * or payout math itself.
   */
  async getReferralSummary(): Promise<Result<ReferralSummaryView, ApiError>> {
    return this.getJson('/referrals/summary', ReferralSummaryView);
  }

  // --- the free front door (prebuilt catalog) --------------------------------

  /**
   * GET the PREBUILT catalog — the ready-made companions the free start offers.
   * The catalog account is edge-resolved config, never a client parameter, so a
   * caller can only ever see published templates.
   */
  async listPrebuiltCatalog(): Promise<Result<PrebuiltCatalogList, ApiError>> {
    return this.getJson('/companions/prebuilt', PrebuiltCatalogList);
  }

  /**
   * POST the free prebuilt provision — clone the chosen template (persona + seed
   * + reference + its pre-generated gallery) into the caller's account. No
   * generation job, no charge; she lands `ready` immediately.
   */
  async provisionPrebuilt(templateId: string): Promise<Result<CompanionView, ApiError>> {
    return this.sendJson('POST', '/companions/prebuilt', { templateId }, CompanionView);
  }

  // --- media job reconciliation + web push -----------------------------------

  /**
   * GET one generation job's status — the POLL fallback behind the WS `media_job`
   * push. A backgrounded tab loses the socket, so a still-pending inline
   * placeholder reconciles against this rather than shimmering forever.
   */
  async getMediaJob(jobId: string): Promise<Result<MediaJobStatusView, ApiError>> {
    return this.getJson(`/media/jobs/${encodeURIComponent(jobId)}`, MediaJobStatusView);
  }

  /**
   * POST this device's Web Push subscription so "she texts you first" can reach
   * it while the tab is closed. The body is the browser's own
   * `PushSubscription.toJSON()` (endpoint + keys); the edge stores it against the
   * authenticated account.
   */
  async subscribePush(
    subscription: PushSubscriptionJSON,
  ): Promise<Result<PushSubscribedView, ApiError>> {
    return this.sendJson('POST', '/push/subscribe', subscription, PushSubscribedView);
  }

  // --- internals ---

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    const bearer = this.token();
    return bearer ? { ...extra, authorization: `Bearer ${bearer}` } : { ...extra };
  }

  private getJson<T>(path: string, schema: ZodType<T>): Promise<Result<T, ApiError>> {
    return requestJson(
      () => this.doFetch(this.url(path), { method: 'GET', headers: this.headers() }),
      schema,
      this.recover,
    );
  }

  private sendJson<T>(
    method: 'POST' | 'PATCH',
    path: string,
    body: unknown,
    schema: ZodType<T>,
    /** Opt out of `401` recovery — the auth section above says which calls do. */
    recover = true,
  ): Promise<Result<T, ApiError>> {
    return requestJson(
      () => this.postJson(method, path, body),
      schema,
      recover ? this.recover : undefined,
    );
  }

  /**
   * The raw JSON write — no envelope, no recovery. Shared by {@link sendJson} and
   * the renewal, so the refresh carries the same base URL + headers as every
   * other write.
   */
  private postJson(
    method: 'POST' | 'PATCH',
    path: string,
    body: unknown,
    signal?: AbortSignal,
  ): Promise<Response> {
    return this.doFetch(this.url(path), {
      method,
      headers: this.headers(JSON_HEADERS),
      body: JSON.stringify(body),
      signal,
    });
  }

  /** POST a multipart body (concept-image upload); the browser owns the content-type. */
  private sendForm<T>(
    path: string,
    body: FormData,
    schema: ZodType<T>,
  ): Promise<Result<T, ApiError>> {
    return requestJson(
      () => this.doFetch(this.url(path), { method: 'POST', headers: this.headers(), body }),
      schema,
      this.recover,
    );
  }
}
